/**
 * Wave 1 locales for high-value STR markets.
 * Default site locale (en-US) has no path prefix.
 */

export type AppLocale = "en-US" | "es-ES" | "es-MX" | "en-CA" | "en-GB";

export type CurrencyCode = "USD" | "EUR" | "MXN" | "CAD" | "GBP";

export type LocaleConfig = {
  code: AppLocale;
  /** URL path segment; null means unprefixed default */
  pathPrefix: string | null;
  htmlLang: string;
  ogLocale: string;
  hreflang: string;
  currency: CurrencyCode;
  countryName: string;
  countryCode: string;
  label: string;
  /** Native language label for language switcher */
  nativeLabel: string;
  /** Audience path segment style */
  audiencePathStyle: "for" | "para";
  platformsMention: "airbnb-vrbo" | "airbnb-booking";
  usesCensusPricing: boolean;
};

export const DEFAULT_LOCALE: AppLocale = "en-US";

export const LOCALES: Record<AppLocale, LocaleConfig> = {
  "en-US": {
    code: "en-US",
    pathPrefix: null,
    htmlLang: "en",
    ogLocale: "en_US",
    hreflang: "en-US",
    currency: "USD",
    countryName: "United States",
    countryCode: "US",
    label: "United States",
    nativeLabel: "English (US)",
    audiencePathStyle: "for",
    platformsMention: "airbnb-vrbo",
    usesCensusPricing: true,
  },
  "es-ES": {
    code: "es-ES",
    pathPrefix: "es-es",
    htmlLang: "es-ES",
    ogLocale: "es_ES",
    hreflang: "es-ES",
    currency: "EUR",
    countryName: "Spain",
    countryCode: "ES",
    label: "España",
    nativeLabel: "Español (España)",
    audiencePathStyle: "para",
    platformsMention: "airbnb-booking",
    usesCensusPricing: false,
  },
  "es-MX": {
    code: "es-MX",
    pathPrefix: "es-mx",
    htmlLang: "es-MX",
    ogLocale: "es_MX",
    hreflang: "es-MX",
    currency: "MXN",
    countryName: "Mexico",
    countryCode: "MX",
    label: "México",
    nativeLabel: "Español (México)",
    audiencePathStyle: "para",
    platformsMention: "airbnb-booking",
    usesCensusPricing: false,
  },
  "en-CA": {
    code: "en-CA",
    pathPrefix: "en-ca",
    htmlLang: "en-CA",
    ogLocale: "en_CA",
    hreflang: "en-CA",
    currency: "CAD",
    countryName: "Canada",
    countryCode: "CA",
    label: "Canada",
    nativeLabel: "English (Canada)",
    audiencePathStyle: "for",
    platformsMention: "airbnb-vrbo",
    usesCensusPricing: false,
  },
  "en-GB": {
    code: "en-GB",
    pathPrefix: "en-gb",
    htmlLang: "en-GB",
    ogLocale: "en_GB",
    hreflang: "en-GB",
    currency: "GBP",
    countryName: "United Kingdom",
    countryCode: "GB",
    label: "United Kingdom",
    nativeLabel: "English (UK)",
    audiencePathStyle: "for",
    platformsMention: "airbnb-vrbo",
    usesCensusPricing: false,
  },
};

/** Prefixed Wave 1 locales only (exclude default en-US). */
export const PREFIXED_LOCALES: AppLocale[] = (
  Object.keys(LOCALES) as AppLocale[]
).filter((code) => LOCALES[code].pathPrefix != null);

export const LOCALE_PATH_PREFIXES = PREFIXED_LOCALES.map(
  (code) => LOCALES[code].pathPrefix as string
);

export const isLocalePathPrefix = (segment: string | undefined): boolean =>
  Boolean(segment && LOCALE_PATH_PREFIXES.includes(segment.toLowerCase()));

export const getLocaleFromPathPrefix = (
  segment: string | undefined
): AppLocale | null => {
  if (!segment) return null;
  const lower = segment.toLowerCase();
  const match = PREFIXED_LOCALES.find(
    (code) => LOCALES[code].pathPrefix === lower
  );
  return match || null;
};

export const getLocaleConfig = (locale: AppLocale): LocaleConfig =>
  LOCALES[locale];
